import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { CopyFromRecipeModal } from "../../straightener-recipes/components/CopyFromRecipeModal";
import { StraightenerRecipeDetailViewer } from "../../straightener-recipes/components/StraightenerRecipeDetailViewer";
import { useStraightenerRecipeValues } from "../../straightener-recipes/hooks/useStraightenerRecipes";
import type {
	ProductNode,
	StraightenerProduct,
	StraightenerRecipeValueRow,
	StraightenerRecipeVersion,
} from "../../straightener-recipes/models/StraightenerRecipe";
import { DeactivateCurrentRecipeModal } from "./DeactivateCurrentRecipeModal";
import { SetCurrentRecipeModal } from "./SetCurrentRecipeModal";
import type { StraightenerRecipeEditor } from "../hooks/useStraightenerRecipeEditor";

interface Props {
	product: StraightenerProduct | null;
	// The recipe being edited; null => a new recipe for `product`.
	recipeId: number | null;
	version: number | null;
	recipeName: string;
	// The version row for the recipe being edited (for Current / PLC / Active
	// flags). Null for a new recipe.
	selectedVersion: StraightenerRecipeVersion | null;
	// Copy-from sources (all products with a current recipe).
	allNodes: ProductNode[];
	// Recipe Copy From opens on (the Trend Display picker's selection).
	copySourceRecipeId?: number | null;
	// Names of the product's other recipes (for the unique-per-product check).
	existingNames?: string[];
	defaultCrew: string;
	editor: StraightenerRecipeEditor;
	// Inline (Trend Display) mode: show a collapse arrow by the title, hide the X.
	collapsible?: boolean;
	// When false, hide the in-editor "Start from" selector (the Recipes page
	// chooses the start-from source in the Add Recipe modal instead).
	showPrefill?: boolean;
	// Read Only users: every write control is disabled / hidden.
	readOnly?: boolean;
	// Reports the editor's unsaved-changes state (for tree-navigation guards).
	onDirtyChange?: (dirty: boolean) => void;
	// Title variant for a new (unsaved) recipe, e.g. "Duplicate Recipe".
	newRecipeTitle?: string;
	onClose: () => void;
}

// The full recipe editor unit: the detail form plus every satellite modal
// (copy-from, duplicate, set-PLC confirm), wired to the shared editor hook.
// The values for an existing recipe are fetched here; a new recipe uses the
// editor's staged `newRecipeValues` (seeded via prefill / copy-from).
export const RecipeEditorPanel = ({
	product,
	recipeId,
	version,
	recipeName,
	selectedVersion,
	allNodes,
	copySourceRecipeId = null,
	existingNames,
	defaultCrew,
	editor,
	collapsible,
	showPrefill = true,
	readOnly = false,
	onDirtyChange,
	newRecipeTitle,
	onClose,
}: Props) => {
	const valuesQ = useStraightenerRecipeValues(recipeId);
	const values = recipeId ? (valuesQ.data ?? []) : editor.newRecipeValues;

	// Deactivating the product's current recipe first asks which sibling should
	// take over as current (or to leave none).
	const [deactivateCurrentOpen, setDeactivateCurrentOpen] = useState(false);
	// Making a recipe current is confirmed first: it changes the setup the
	// operators run against. Open on an existing recipe, or holding the payload
	// of a new one that is being saved straight into current.
	const [setCurrentPending, setSetCurrentPending] = useState<
		| { kind: "existing"; recipeId: number }
		| {
				kind: "new";
				crew: string | null;
				name: string | null;
				comments: string | null;
				values: StraightenerRecipeValueRow[];
		  }
		| null
	>(null);
	// Other active, complete recipes of this product — the eligible successors.
	const successors = useMemo<StraightenerRecipeVersion[]>(() => {
		if (!product) return [];
		const node = allNodes.find((n) => n.productSizeId === product.Id);
		return (node?.versions ?? []).filter(
			(v) => v.Id !== recipeId && v.IsActive && !v.IsDraft,
		);
	}, [allNodes, product, recipeId]);

	const handleToggleActive = (active: boolean) => {
		if (!recipeId) return;
		// Turning a current recipe off needs a current hand-off decision.
		if (!active && selectedVersion?.IsCurrent) {
			setDeactivateCurrentOpen(true);
			return;
		}
		editor.setActive({ recipeId, active });
	};

	const handleSaveNew = (
		crew: string | null,
		name: string | null,
		comments: string | null,
		vals: StraightenerRecipeValueRow[],
	) => {
		if (!product) return;
		editor.saveRecipe({
			productSizeId: product.Id,
			name,
			crew,
			comments,
			values: vals,
		});
	};

	const handleSaveNewAndSetCurrent = (
		crew: string | null,
		name: string | null,
		comments: string | null,
		vals: StraightenerRecipeValueRow[],
	) => {
		if (!product) return;
		setSetCurrentPending({ kind: "new", crew, name, comments, values: vals });
	};

	const confirmSetCurrent = () => {
		const pending = setCurrentPending;
		setSetCurrentPending(null);
		if (!pending) return;
		if (pending.kind === "existing") {
			editor.setCurrent(pending.recipeId);
			return;
		}
		if (!product) return;
		editor.saveRecipeAndSetCurrent({
			productSizeId: product.Id,
			name: pending.name,
			crew: pending.crew,
			comments: pending.comments,
			values: pending.values,
		});
	};

	const handleSaveCurrent = (
		crew: string | null,
		name: string | null,
		comments: string | null,
		vals: StraightenerRecipeValueRow[],
	) => {
		if (!recipeId) return;
		editor.updateRecipe({ recipeId, name, crew, comments, values: vals });
	};

	return (
		<>
			<StraightenerRecipeDetailViewer
				product={product}
				recipeId={recipeId}
				version={version}
				values={values}
				// React Query v4: `isLoading` stays true while the query is disabled,
				// so for a new recipe the loading state is the prefill's, not the
				// (never-run) values query.
				isLoadingValues={recipeId ? valuesQ.isLoading : editor.isPrefilling}
				isSaving={editor.isSaving || editor.isUpdating}
				// A saved recipe opens with the crew it was saved with; only a new
				// one falls back to the crew that is running now.
				defaultCrew={selectedVersion?.Crew ?? defaultCrew}
				defaultName={recipeName}
				defaultComments={selectedVersion?.Comments ?? ""}
				existingNames={existingNames}
				onSave={handleSaveNew}
				onSaveAndSetCurrent={handleSaveNewAndSetCurrent}
				onSaveCurrent={handleSaveCurrent}
				onPrefill={showPrefill ? editor.prefill : undefined}
				copySourceLabel={editor.copySourceLabel}
				isCurrent={selectedVersion?.IsCurrent ?? false}
				isPlcCurrent={selectedVersion?.IsPlcCurrent ?? false}
				isActive={selectedVersion?.IsActive ?? true}
				isDraft={selectedVersion?.IsDraft ?? false}
				onSetCurrent={() =>
					recipeId && setSetCurrentPending({ kind: "existing", recipeId })
				}
				onSetPlcCurrent={() => recipeId && editor.setPlcConfirmId(recipeId)}
				onToggleActive={handleToggleActive}
				collapsible={collapsible}
				newRecipeTitle={newRecipeTitle}
				readOnly={readOnly}
				onDirtyChange={onDirtyChange}
				onClose={onClose}
			/>

			<Dialog
				open={editor.plcConfirmId != null}
				onClose={() => editor.setPlcConfirmId(null)}
				maxWidth="xs"
			>
				<DialogTitle sx={{ fontWeight: 600 }}>
					Set recipe in the PLC?
				</DialogTitle>
				<DialogContent>
					<Typography>
						This will change the <strong>straightener machine setup</strong> —
						the recipe loaded in the PLC. Continue?
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button
						variant="contained"
						onClick={() => editor.setPlcConfirmId(null)}
						sx={{
							backgroundColor: "#e0e0e0",
							color: "rgba(0, 0, 0, 0.87)",
							"&:hover": { backgroundColor: "#d5d5d5" },
						}}
					>
						CANCEL
					</Button>
					<Button
						variant="contained"
						color="success"
						onClick={() => {
							if (editor.plcConfirmId)
								editor.setPlcCurrent(editor.plcConfirmId);
							editor.setPlcConfirmId(null);
						}}
					>
						SET PLC CURRENT
					</Button>
				</DialogActions>
			</Dialog>

			<CopyFromRecipeModal
				open={editor.copyOpen}
				nodes={allNodes}
				defaultProductId={product?.Id ?? null}
				defaultRecipeId={copySourceRecipeId}
				isCopying={editor.isCopying}
				onCopy={editor.copyFrom}
				onCancel={() => editor.setCopyOpen(false)}
			/>

			<DeactivateCurrentRecipeModal
				open={deactivateCurrentOpen}
				productLabel={
					product ? `(${product.ProductTypeName}) ${product.Name}` : ""
				}
				recipeLabel={
					selectedVersion
						? `${selectedVersion.Name ?? "(unnamed)"} · v${selectedVersion.Version}`
						: ""
				}
				successors={successors}
				onConfirm={(newCurrentRecipeId) => {
					setDeactivateCurrentOpen(false);
					if (recipeId)
						editor.setActive({
							recipeId,
							active: false,
							newCurrentRecipeId,
						});
				}}
				onCancel={() => setDeactivateCurrentOpen(false)}
			/>

			<SetCurrentRecipeModal
				open={setCurrentPending != null}
				productLabel={
					product ? `(${product.ProductTypeName}) ${product.Name}` : ""
				}
				recipeLabel={
					setCurrentPending?.kind === "new"
						? (setCurrentPending.name ?? "(unnamed)")
						: selectedVersion
							? `${selectedVersion.Name ?? "(unnamed)"} · v${selectedVersion.Version}`
							: ""
				}
				busy={editor.isSaving}
				onConfirm={confirmSetCurrent}
				onCancel={() => setSetCurrentPending(null)}
			/>
		</>
	);
};
